"use client";

import * as React from "react";
import { ShieldCheck, Wrench, Clock, BadgeCheck, PhoneCall, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const faqs = [{
  question: "How often should I schedule routine home maintenance?",
  answer: "We recommend a comprehensive inspection and tune-up at least twice a year—typically in the spring and fall. This proactive approach prepares your home's critical systems for extreme weather conditions, ensures peak efficiency, and helps prevent unexpected breakdowns."
}, {
  question: "What seasonal tasks are included in your maintenance packages?",
  answer: "Our seasonal packages are tailored to the time of year. They typically cover HVAC filter replacement and system calibration, gutter cleaning and inspection, plumbing leak checks, electrical panel safety inspections, and water heater flushing to remove sediment buildup."
}, {
  question: "What are the benefits of signing up for a maintenance package?",
  answer: "Maintenance plans connect homeowners with local pros for scheduled check-ups, helping maintain home systems, prevent unexpected breakdowns, and keep equipment operating efficiently year-round."
}, {
  question: "What exactly is covered under the standard maintenance plan?",
  answer: "A standard plan typically covers bi-annual HVAC tune-ups, a periodic plumbing inspection, and an electrical safety review coordinated through independent local service pros."
}, {
  question: "Do you offer support for urgent breakdowns?",
  answer: "Local pros set their own service hours and availability. When submitting a request through the marketplace, ask your pro about prompt scheduling options."
}, {
  question: "How much can I expect to save with regular maintenance?",
  answer: "Regular maintenance helps systems run efficiently, extends the operational lifespan of major equipment, and catches small problems before they escalate into major repairs."
}, {
  question: "How are pros verified on the platform?",
  answer: "Handy Heroes is a marketplace connecting homeowners with independent local pros. Pros join through network onboarding. Confirm credentials and terms with your pro before work begins."
}];
const trustFeatures = [{
  icon: ShieldCheck,
  text: "Vetted Pros"
}, {
  icon: BadgeCheck,
  text: "Upfront Quotes"
}, {
  icon: Clock,
  text: "Flexible Times"
}, {
  icon: Wrench,
  text: "Clear Terms"
}];
export default function MaintenanceFAQ() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: faqRef,
    isVisible: isFaqVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <section id="maintenancefaq" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        
        {}
        <div ref={headerRef} className={cn("flex flex-col items-center text-center mb-12 md:mb-16 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-sm">
            <ShieldCheck className="mr-1.5 size-4 text-primary" />
            Expert Knowledge Base
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4 max-w-3xl">
            Home Maintenance <span className="text-primary">FAQs</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl leading-relaxed">
            Everything you need to know about keeping your home in perfect condition, from seasonal schedules to package benefits.
          </p>
        </div>

        {}
        <div ref={faqRef} className={cn("max-w-3xl mx-auto bg-card rounded-2xl p-6 md:p-8 shadow-sm border border-border transition-all duration-700 delay-100 ease-out", isFaqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`} data-index={index}>
                <AccordionTrigger className="text-left font-default text-base md:text-lg font-semibold text-card-foreground hover:text-primary transition-colors py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {}
        <div ref={ctaRef} className={cn("mt-16 md:mt-24 max-w-4xl mx-auto transition-all duration-700 delay-200 ease-out", isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 mb-12">
            {trustFeatures.map((feature, index) => <div key={index} data-index={index} className="flex flex-col items-center text-center space-y-2">
                <div className="bg-primary/10 p-3 rounded-lg text-primary mb-2">
                  <feature.icon className="size-6" />
                </div>
                <span className="font-default font-medium text-foreground text-sm md:text-base">
                  {feature.text}
                </span>
              </div>)}
          </div>

          <div className="bg-secondary rounded-2xl p-8 md:p-12 text-center relative overflow-hidden shadow-lg">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50"></div>
            <h3 className="font-default text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
              Still have questions?
            </h3>
            <p className="text-secondary-foreground/80 text-lg mb-8 max-w-xl mx-auto">
              Our home service experts are standing by to help you choose the right maintenance plan for your property.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" asChild className="w-full sm:w-auto">
                <Link to="#bookingrequest">
                  <PhoneCall className="mr-2 size-5" />
                  Contact Support
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="w-full sm:w-auto bg-transparent border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10">
                <Link to="#bookingrequest">
                  <Mail className="mr-2 size-5" />
                  Email Us
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}